import React, { useCallback, useLayoutEffect, useMemo } from "react";
import { HeaderButton, HeaderButtonLoading } from "../components";
import { NativeStackNavigationOptions } from "@react-navigation/native-stack";
import { Platform, View } from "react-native";
import { Box, useTheme } from "arkh";
import { MoreButton, MoreItem } from "./MoreButton";
import { useNavigation } from "@react-navigation/native";
import { HeaderBackButton } from "@react-navigation/elements";
export type { MoreItem };

type Args = (
  | {
      type: "DONE";
      onDone: () => void;
      more?: MoreItem[];
      saveStatus?: "saving" | "saved" | "error";
    }
  | {
      type: "CREATE-INIT";
      onCancel: () => void;
    }
  | {
      type: "CREATE";
      onCancel: () => void;
      onSave: () => void;
      saveLoading: boolean;
      disabled?: boolean;
      createButtonLabel?: string;
    }
  | {
      type: "SAVE-INIT";
      onCancel: () => void;
    }
  | {
      type: "SAVE";
      onCancel: () => void;
      onSave?: () => void;
      saveLoading: boolean;
      disabled?: boolean;
      more?: MoreItem[];
    }
  | {
      type: "CLOSE" | "CANCEL";
      onClose: () => void;

      more?: MoreItem[];
    }
  | {
      type: "BACK";
      onBack?: () => void;

      more?: MoreItem[];
    }
  | { type: "DEFAULT" }
) & {
  title?: string;
  headerTitle?: NativeStackNavigationOptions["headerTitle"];
  disableBorder?: boolean;
  defaultContentStyle?: boolean;
};
export function useDefaultScreenOptions() {
  const getOptions = useNavigationHeaderOptions();
  return useMemo(() => getOptions({ type: "DEFAULT" }), [getOptions]);
}

export function useNavigationHeaderOptions() {
  const theme = useTheme();

  return useCallback(
    (props: Args): NativeStackNavigationOptions => {
      return {
        headerStyle: {
          backgroundColor: theme["surface-primary"],
        },
        contentStyle:
          props.defaultContentStyle || props.type === "DEFAULT"
            ? {
                backgroundColor: theme["surface-background-light"],
              }
            : undefined,
        headerShadowVisible: !props.disableBorder,
        title: props.title ?? "",
        headerTitle: props.headerTitle,
        headerTitleAlign: "center",
        headerRight: () => {
          switch (props.type) {
            case "DEFAULT":
              return null;
            case "CLOSE":
            case "BACK":
            case "DONE":
            case "CANCEL":
              if (!props.more) {
                return null;
              }
              return (
                <MoreButton items={props.more}>
                  {(onOpen) => <HeaderButton type="MORE" onPress={onOpen} />}
                </MoreButton>
              );

            case "SAVE-INIT":
              return null;
            case "CREATE-INIT":
              return <HeaderButton type="CREATE" disabled emphazise />;
            case "CREATE":
              return props.saveLoading ? (
                <HeaderButtonLoading />
              ) : (
                <HeaderButton
                  type="CREATE"
                  customLabel={props.createButtonLabel}
                  disabled={props.disabled}
                  emphazise
                  onPress={() => {
                    props.onSave();
                  }}
                />
              );

            case "SAVE":
              return (
                <View
                  style={{
                    flexDirection: "row",
                  }}
                >
                  {props.more ? (
                    <Box
                      spaceRight={Platform.OS === "web" ? "none" : "large"}
                      style={{ justifyContent: "center" }}
                    >
                      <MoreButton items={props.more}>
                        {(onOpen) => (
                          <HeaderButton
                            color="text-primary"
                            type="MORE"
                            onPress={onOpen}
                          />
                        )}
                      </MoreButton>
                    </Box>
                  ) : null}
                  {props.saveLoading ? (
                    <HeaderButtonLoading />
                  ) : props.onSave ? (
                    <HeaderButton
                      type="SAVE"
                      disabled={props.disabled}
                      onPress={() => {
                        props.onSave?.();
                      }}
                    />
                  ) : null}
                </View>
              );
            default:
              return null;
          }
        },
        headerLeft:
          props.type === "DEFAULT"
            ? undefined
            : Platform.OS === "android" &&
                (props.type === "CLOSE" ||
                  props.type === "BACK" ||
                  props.type === "CANCEL")
              ? undefined
              : (p) => {
                  switch (props.type) {
                    case "BACK":
                      return props.onBack ? (
                        <HeaderButton type="BACK" onPress={props.onBack} />
                      ) : (
                        <HeaderBackButton {...p} />
                      );
                    case "DONE":
                      return (
                        <HeaderButton
                          type="DONE"
                          onPress={() => {
                            props.onDone();
                          }}
                        />
                      );
                    case "CREATE-INIT":
                    case "CREATE":
                    case "SAVE-INIT":
                    case "SAVE":
                      return (
                        <HeaderButton
                          type="CANCEL"
                          onPress={() => {
                            props.onCancel();
                          }}
                        />
                      );
                    case "CANCEL":
                      return (
                        <HeaderButton
                          type="CANCEL"
                          onPress={() => {
                            props.onClose();
                          }}
                        />
                      );
                    case "CLOSE":
                      return (
                        <HeaderButton
                          type="CLOSE"
                          onPress={() => {
                            props.onClose();
                          }}
                        />
                      );
                  }
                },
      };
    },
    [theme]
  );
}

export function useNavigationHeader(opts: Args) {
  const nav = useNavigation();
  const getOptions = useNavigationHeaderOptions();

  useLayoutEffect(
    () => nav.setOptions(getOptions(opts)),
    [getOptions, nav, opts]
  );
}
